"use client";

import type { ElementType, ReactNode } from "react";
import type { SectionState } from "../navigation";

/* -------------------------------------------------------------------------- */
/* OrgSectionCard                                                              */
/* -------------------------------------------------------------------------- */

export interface OrgSectionCardProps {
  /** Section title displayed on the card (e.g. "Foundational Inputs"). */
  title: string;
  /** Short status summary (e.g. "3/8 Complete" or "4 payers"). */
  statusText: string;
  /** Validation state — drives border/background colour. */
  state: SectionState;
  /**
   * SVG `<path>` / `<circle>` etc. children rendered inside an 18×18 SVG
   * icon wrapper.  Pass only the inner shapes, not the outer `<svg>`.
   */
  icon: ReactNode;
  /**
   * CSS custom-property name for the icon's tone colour
   * (e.g. `"--accent-teal"`).  Defaults to `"--brand-navy"`.
   */
  iconTone?: string;
  /**
   * Destination URL — forwarded as `href` to both the default `"a"` element
   * and any custom component supplied via `as` (e.g. Next.js `Link`).
   */
  href: string;
  /**
   * Override the root element type.  Use this to pass a router `Link`
   * without importing a routing library into the package.
   *
   * @example
   * ```tsx
   * import Link from "next/link";
   * <OrgSectionCard as={Link} href="/organization/identity" ... />
   * ```
   */
  as?: ElementType;
}

const STATE_CLS: Record<SectionState, { card: string; icon: string }> = {
  complete: {
    card: "border-[color-mix(in_oklab,var(--accent-teal,#00b894)_35%,transparent)] hover:border-[color-mix(in_oklab,var(--accent-teal,#00b894)_60%,transparent)]",
    icon: "bg-[color-mix(in_oklab,var(--accent-teal,#00b894)_12%,transparent)] text-[var(--accent-teal,#00b894)]",
  },
  red: {
    card: "border-[color-mix(in_oklab,var(--accent-red,#dc3545)_35%,transparent)] hover:border-[color-mix(in_oklab,var(--accent-red,#dc3545)_60%,transparent)]",
    icon: "bg-[color-mix(in_oklab,var(--accent-red,#dc3545)_10%,transparent)] text-[var(--accent-red,#dc3545)]",
  },
  yellow: {
    card: "border-[color-mix(in_oklab,var(--accent-gold,#f0ad00)_40%,transparent)] hover:border-[color-mix(in_oklab,var(--accent-gold,#f0ad00)_70%,transparent)]",
    icon: "bg-[color-mix(in_oklab,var(--accent-gold,#f0ad00)_12%,transparent)] text-[var(--accent-gold-text,var(--fg-secondary))]",
  },
  gray: {
    card: "border-(--border-default) hover:border-[color-mix(in_oklab,var(--fg-muted)_50%,transparent)]",
    icon: "bg-(--bg-canvas) text-(--fg-muted)",
  },
};

/**
 * Clickable navigation card for the Organisation Overview grid.
 *
 * Decoupled from any specific routing library — pass `as={Link}` from
 * react-router-dom or next/link as needed.
 */
export function OrgSectionCard({
  title,
  statusText,
  state,
  icon,
  iconTone,
  href,
  as: Root = "a",
}: OrgSectionCardProps) {
  const cls = STATE_CLS[state];
  const iconStyle = iconTone
    ? {
        color: `var(${iconTone})`,
        backgroundColor: `color-mix(in oklab, var(${iconTone}) 12%, transparent)`,
      }
    : undefined;

  const navProps: any = { href };

  return (
    <Root
      {...navProps}
      className={[
        "flex items-center gap-3 p-4 rounded-[8px] border",
        "bg-(--bg-white)",
        "transition-[border-color,box-shadow] duration-150",
        "hover:shadow-[0_2px_8px_rgba(10,16,32,0.08)]",
        "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-2",
        "no-underline",
        cls.card,
      ].join(" ")}
    >
      {/* Icon */}
      <span
        className={[
          "inline-flex items-center justify-center",
          "w-[32px] h-[32px] rounded-[6px] shrink-0",
          iconTone ? "" : cls.icon,
        ].join(" ")}
        style={iconStyle}
        aria-hidden
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {icon}
        </svg>
      </span>

      {/* Text */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-sm font-medium text-(--fg-primary) leading-snug">
          {title}
        </span>
        <span className="text-xs text-(--fg-muted)">{statusText}</span>
      </div>

      {/* Trailing indicator */}
      <span className="shrink-0 text-(--fg-muted)" aria-hidden>
        {state === "complete" ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent-teal, #00b894)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12.5l4 4 10-10" />
          </svg>
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4L22 20H2L12 4Z" />
            <path d="M12 11v3" />
            <path d="M12 17h.01" />
          </svg>
        )}
      </span>
    </Root>
  );
}
